import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import {AutoBatcher, BaseApplicationComponent, CachedFetcher} from "app/services/domain/base.service";
import type {AppDomainContext} from "app/services/domain/domain.module";
import {ScanProgressState, WafAssessmentRunBuilder, WafAssessmentRunExtended, WafAssessmentRunHistoryExtended, WafAssessmentRunPolicyScanExtended, WafAssessmentRunQuestionExtended} from "app/services/domain/waf-assessment-run.service";
import {AttachmentsApi} from "app/services/proxy/api/AttachmentsApi";
import * as Models from "app/services/proxy/model/models";
import {Comment} from "app/shared/comments/comments.component";
import {WafGetStatusFriendly} from "app/waf-frameworks/waf-framework/waf-framework-elements";
import {WafFrameworkQueryModel} from "app/waf-frameworks/waf-framework/waf-framework-filtering";

import {LocalStorageCacheStore} from "framework/services/cache.service";
import {Lookup, LookupHelpers, UtilsService} from "framework/services/utils.service";
import {ChartColorUtilities} from "framework/ui/charting/core/colors";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {mapInParallel} from "framework/utils/concurrency";

import {Subject} from "rxjs";

export enum WafRoles
{
    WorkloadOwner      = "workload_owner",
    AssessmentApprover = "assessments_approver",
    AssessmentMember   = "assessments_member",
    AssessmentReader   = "assessments_reader"
}

@Injectable()
export class WafFrameworksService
{
    public static readonly WorkloadOwnerRoles      = [WafRoles.WorkloadOwner];
    public static readonly AssessmentOwnerRoles    = [
        WafRoles.WorkloadOwner,
        WafRoles.AssessmentApprover
    ];
    public static readonly AssessmentAssignerRoles = [
        WafRoles.WorkloadOwner,
        WafRoles.AssessmentApprover,
        WafRoles.AssessmentMember
    ];

    private readonly m_frameworkFetchers: Lookup<WafTenantFetcher> = {};
    private readonly m_cache: CachedFetcher                        = new CachedFetcher(new LocalStorageCacheStore("WAF_FRAMEWORK"));
    
    private readonly m_toggledQuestionIds = new Set<string>();

    constructor(public readonly api: ApiService)
    {}

     public addToggledQuestion(questionId: string): void
    {
        this.m_toggledQuestionIds.add(questionId);
    }

    public removeToggledQuestion(questionId: string): void
    {
        this.m_toggledQuestionIds.delete(questionId);
    }

    public isQuestionToggled(questionId: string): boolean
    {
        return this.m_toggledQuestionIds.has(questionId);
    }

    public revertToggledQuestionsForPillar(pillar: WellArchitectedFrameworkPillarExtended): void
    {
        if (!pillar?.questions) return;
        for (const question of pillar.questions)
        {
            const uniqueKey = `${question.model?.pillar_id}:${question.id}`;
            if (this.m_toggledQuestionIds.has(uniqueKey))
            {
                question.questionApplicable = false;
                this.m_toggledQuestionIds.delete(uniqueKey);
            }
        }
    }

    getFrameworksFetcher(tenantId: string): WafTenantFetcher
    {
        if (tenantId && !this.m_frameworkFetchers[tenantId])
        {
            this.m_frameworkFetchers[tenantId] = new WafTenantFetcher(tenantId, this);
        }

        return this.m_frameworkFetchers[tenantId];
    }

    async updateViolations(issues: PolicyIssue[])
    {
        const cloudAccountIds = new Set<string>();
        for (const issue of issues)
        {
            const scans = issue.filteredScans.map((scan: WafAssessmentRunPolicyScanExtended) => scan.model.cloud_account_id);
            scans.forEach((accountId: string) => cloudAccountIds.add(accountId));
        }

        for (const issue of Object.values(issues))
        {
            for (const scan of issue.filteredScans)
            {
                if (scan.violations)
                {
                    scan.violations = [...scan.violations];
                }
            }
        }
    }

    public async getWorkloadQueryList(ids: string[]): Promise<string[]>
    {
        let filters: Models.WorkloadTierConfiguration = Models.WorkloadTierConfiguration.newInstance({});
        filters.cloud_account_id                      = ids;
        let resourceIds: string[]                     = [];

        const context = Models.ListContext.newInstance({
                                                           batch_size  : 10000,
                                                           batch_offset: 0,
                                                           total       : 0
                                                       });

        const request = Models.WorkloadQueryRequest.newInstance({
                                                                    list_context: context,
                                                                    filters     : filters,
                                                                    sort        : null
                                                                });


        while (request.list_context)
        {
            const response       = await this.api.workloadQueryApi.execute(request);
            request.list_context = response.next_list_context;
            resourceIds.push(...response.results);
        }
        return resourceIds;
    }

    async frameworkBatch(tenant_id: string,
                         ids: Models.RecordIdentity[]): Promise<Models.WellArchitectedFramework[]>
    {
        return this.m_cache.batch(ids, (framework) =>
        {
            Models.WellArchitectedFramework.fixupPrototype(framework);
            return framework.updated_on;
        }, (req) => this.api.wellArchitectedFrameworkApi.frameworkBatch(tenant_id, req));
    }

    async questionBatch(tenant_id: string,
                        ids: Models.RecordIdentity[]): Promise<Models.WellArchitectedFrameworkQuestion[]>
    {
        return this.m_cache.batch(ids, (question) =>
        {
            Models.WellArchitectedFrameworkQuestion.fixupPrototype(question);
            return question.updated_on;
        }, (req) => this.api.wellArchitectedFrameworkApi.questionBatch(tenant_id, req));
    }

    async bestPracticeBatch(tenant_id: string,
                            ids: Models.RecordIdentity[]): Promise<Models.WellArchitectedFrameworkBestPractice[]>
    {
        return this.m_cache.batch(ids, (best_practice) =>
        {
            Models.WellArchitectedFrameworkBestPractice.fixupPrototype(best_practice);
            return null;
        }, (req) => this.api.wellArchitectedFrameworkApi.bestPracticeBatch(tenant_id, req));
    }

    async deleteFramework(tenantId: string,
                          frameworkId: string)
    {
        return this.api.wellArchitectedFrameworkApi.frameworkDelete(tenantId, frameworkId);
    }

    async frameworkSync(frameworkId: string,
                        tenantId: string): Promise<Models.WellArchitectedFramework>
    {
        return this.api.wellArchitectedFrameworkApi.frameworkSync(frameworkId, tenantId);
    }

    public clearCache()
    {
        this.m_cache.clearCache();
    }
}

export class WafTenantFetcher
{
    private m_frameworkBatcher    = new AutoBatcher<Models.WellArchitectedFramework>(100, (ids) => this.frameworks.frameworkBatch(this.tenantId, ids));
    private m_questionBatcher     = new AutoBatcher<Models.WellArchitectedFrameworkQuestion>(100, (ids) => this.frameworks.questionBatch(this.tenantId, ids));
    private m_bestPracticeBatcher = new AutoBatcher<Models.WellArchitectedFrameworkBestPractice>(100, (ids) => this.frameworks.bestPracticeBatch(this.tenantId, ids));

    private m_frameworks: Lookup<Models.WellArchitectedFramework>;
    private readonly m_policyUrisByFramework: Lookup<Set<string>> = {};

    private readonly m_assessmentRunBuilders: Lookup<WafAssessmentRunBuilder> = {};
    private readonly m_history: Lookup<WafAssessmentRunHistoryExtended[]>     = {};

    constructor(public readonly tenantId: string,
                private readonly frameworks: WafFrameworksService)
    {}

    resetHistoryCache(assessmentRunId: string)
    {
        this.m_history[assessmentRunId]               = null;
        this.m_assessmentRunBuilders[assessmentRunId] = null;
    }

    async getAllHistory(appDomain: AppDomainContext,
                        assessmentRunId: string): Promise<WafAssessmentRunHistoryExtended[]>
    {
        let history = this.m_history[assessmentRunId];
        if (!history)
        {
            history                         = await appDomain.wafAssessmentRuns.loadAllHistory(assessmentRunId);
            this.m_history[assessmentRunId] = history;
        }

        return history;
    }

    async getAssessmentRunBuilder(appDomain: AppDomainContext,
                                  run: Models.WellArchitectedFrameworkAssessmentRun): Promise<WafAssessmentRunBuilder>
    {
        let builder = this.m_assessmentRunBuilders[run.sysId];
        if (!builder)
        {
            let [frameworks, history, versionExt] = await Promise.all([
                                                                          this.getFrameworks(),
                                                                          this.getAllHistory(appDomain, run.sysId),
                                                                          appDomain.wafAssessmentVersions.getExtendedByIdentity(run.assessment_version)
                                                                      ]);

            const relevantFrameworks = new Set(versionExt.model.details.framework_ids);
            frameworks               = frameworks.filter((framework) => relevantFrameworks.has(framework.framework_id));
            if (frameworks.length && history)
            {
                builder                                 = new WafAssessmentRunBuilder(frameworks, history);
                this.m_assessmentRunBuilders[run.sysId] = builder;
            }
        }

        return builder;
    }

    //--//

    async getQuestions(questionRecords: Models.RecordIdentity[]): Promise<Models.WellArchitectedFrameworkQuestion[]>
    {
        return mapInParallel(questionRecords || [], (record) => this.m_questionBatcher.getByIdentity(record));
    }

    async getBestPractices(bestPracticeRecords: Models.RecordIdentity[]): Promise<Models.WellArchitectedFrameworkBestPractice[]>
    {
        const bestPractices = await mapInParallel(bestPracticeRecords || [], (record) => this.m_bestPracticeBatcher.getByIdentity(record));
        if (bestPractices)
        {
            for (const bp of bestPractices)
            {
                for (const policyUri of bp?.policy_uris ?? [])
                {
                    const uris = LookupHelpers.ensureBaseState(this.m_policyUrisByFramework, bp.framework_id, () => new Set());
                    uris.add(policyUri);
                }
            }
        }

        return bestPractices;
    }

    getPolicyUris(frameworkId: string): Set<string>
    {
        return new Set(this.m_policyUrisByFramework[frameworkId]);
    }

    //--//

    async getFrameworks(): Promise<Models.WellArchitectedFramework[]>
    {
        await this.ensureFrameworks();
        return Object.values(this.m_frameworks || {});
    }

    async getVisibleFrameworks(): Promise<Models.WellArchitectedFramework[]>
    {
        const frameworks = await this.getFrameworks();
        return frameworks.filter((f) => f.status == Models.WellArchitectedFrameworkStatus.Active);
    }

    async getFrameworkById(id: string): Promise<Models.WellArchitectedFramework>
    {
        await this.ensureFrameworks();
        return this.m_frameworks?.[id];
    }

    async createFramework(framework: Models.WellArchitectedFramework): Promise<Models.WellArchitectedFramework>
    {
        await this.ensureFrameworks();
        const created                           = await this.frameworks.api.wellArchitectedFrameworkApi.frameworkCreate(this.tenantId, framework);
        this.m_frameworks[created.framework_id] = created;
        return created;
    }

    async updateFramework(framework: Models.WellArchitectedFramework): Promise<Models.WellArchitectedFramework>
    {
        await this.ensureFrameworks();
        if (this.m_frameworks[framework.framework_id])
        {
            const updated                             = await this.frameworks.api.wellArchitectedFrameworkApi.frameworkUpdate(this.tenantId, framework);
            this.m_frameworks[framework.framework_id] = updated;
            return updated;
        }

        return null;
    }

    async deleteFramework(id: string)
    {
        await this.ensureFrameworks();
        if (this.m_frameworks[id])
        {
            await this.frameworks.deleteFramework(this.tenantId, id);
            delete this.m_frameworks[id];
        }
    }

    private async ensureFrameworks(refreshFramework: boolean = true)
    {
        if (!this.m_frameworks || refreshFramework)
        {
            const frameworkRecords = await this.frameworks.api.wellArchitectedFrameworkApi.frameworkList(this.tenantId);
            if (frameworkRecords)
            {
                const frameworks = await mapInParallel(frameworkRecords, (record) => this.m_frameworkBatcher.getByIdentity(record));
                if (frameworks)
                {
                    this.m_frameworks = {};
                    for (const framework of frameworks) this.m_frameworks[framework.framework_id] = framework;
                }
            }
        }
    }

    public clearCache()
    {
        this.frameworks.clearCache();
    }

    public async refreshFramework(frameworkId: string)
    {
        this.clearCache();
        await this.ensureFrameworks(true);
        return this.m_frameworks[frameworkId];
    }

}

//--//

export class WellArchitectedFrameworkPillarScore
{
    constructor(public readonly model: Models.WellArchitectedFrameworkPillar,
                public readonly icon: string,
                public readonly score: number,
                public readonly numAutomaticIssues: number,
                public readonly unverifiedAutomatedBestPractices: number,
                public readonly unverifiedManualBestPractices: number,
                public readonly applicable: boolean)
    {}
}

export class WellArchitectedFrameworkExtendedScores
{
    constructor(public readonly historyId: string,
                public readonly overallScore: number,
                public readonly pillarScores: WellArchitectedFrameworkPillarScore[])
    {}
}

export class WellArchitectedFrameworkExtended
{
    get id(): string
    {
        return this.model.framework_id;
    }

    runExt: WafAssessmentRunExtended;

    get isCustom(): boolean
    {
        return this.model.framework_type === Models.WellArchitectedFrameworkType.Custom;
    }

    get handleNoneOfTheAbove(): Models.WellArchitectedFrameworkHandleNoneOfTheAbove
    {
        return this.model.handle_none_of_the_above;
    }

    private m_someMatchFilter: boolean = true;
    get someMatchFilter(): boolean
    {
        return this.m_someMatchFilter;
    }

    get score(): number
    {
        let cumPillarScore     = 0;
        let cumNumApplicableBp = 0;
        for (const pillar of this.m_pillars)
        {
            cumPillarScore += pillar.score * pillar.numBp;
            cumNumApplicableBp += pillar.numBp;
        }

        return cumPillarScore / cumNumApplicableBp || 0;
    }

    get progress(): number
    {
        let totalBp     = 0;
        let completedBp = 0;
        for (const pillar of this.m_pillars)
        {
            totalBp += pillar.numBp;
            completedBp += pillar.numCompletedBp;
        }

        return 100 * completedBp / totalBp || 0;
    }

    get latestEntryExt(): WafAssessmentRunHistoryExtended
    {
        return this.runExt.latestEntryExt;
    }

    get runHasActiveScan(): boolean
    {
        return this.runExt.scanStatus === ScanProgressState.InProgress;
    }

    get hasScanningPolicies(): boolean
    {
        return !!this.m_scanningPolicyUris?.size;
    }

    get scanStatus(): ScanProgressState
    {
        return this.hasScanningPolicies || !this.runExt.firstScanComplete ? this.runExt.scanStatus : ScanProgressState.Complete;
    }

    get scanStatusDate(): Date
    {
        if (this.hasScanningPolicies)
        {
            const latestEntry = this.latestEntryExt.model;
            if (!latestEntry.background_job) return null;

            let max: Date;
            for (const scan of latestEntry.policy_scans || [])
            {
                if (!max || MomentHelper.compareDates(max, scan.completed, true) < 0) max = scan.completed;
            }

            return max || latestEntry.createdOn;
        }
        else
        {
            const dates = UtilsService.mapLookup(this.m_policyState, (key,
                                                                      value) => value.context.framework_id === this.id ? value.model?.completed?.valueOf() : null, true);
            return dates.length ? new Date(Math.max(...dates)) : null;
        }
    }

    get lastScan(): Date
    {
        const dates = UtilsService.mapLookup(this.m_policyState, (key,
                                                                  value) => value.model?.completed?.valueOf(), true);

        return dates.length ? new Date(Math.max(...dates)) : null;
    }

    get policyLabel(): string
    {
        return null;
    }

    get policies(): WafAssessmentRunPolicyScanExtended[]
    {
        const allPolicies = UtilsService.mapLookup(this.m_policyState, (key,
                                                                        value) => value.model ? value : null, true);

        return allPolicies.filter(policyScan =>
                                  {
                                      const bpsWithPolicy = this.pillars.flatMap(p => p.questions.flatMap(q => q.bestPractices))
                                                                .filter(bp =>
                                                                            bp.model.policy_uris?.includes(policyScan.model.policy_uri)
                                                                );

                                      if (!bpsWithPolicy.length) return true;

                                      const hasVisiblePolicyBp = bpsWithPolicy.some(bp => WellArchitectedFrameworkLayerWithState.hasVisiblePoliciesForBestPractice(bp, true));

                                      return hasVisiblePolicyBp;
                                  });
    }

    set pillars(pillars: WellArchitectedFrameworkPillarExtended[])
    {
        this.m_pillars = pillars;
    }

    get pillars(): WellArchitectedFrameworkPillarExtended[]
    {
        return this.m_pillars;
    }

    private readonly m_pillarLookup: Lookup<WellArchitectedFrameworkPillarExtended>;
    private readonly m_scoresLookup: Lookup<WellArchitectedFrameworkExtendedScores> = {};
    get scoresLookup(): Lookup<WellArchitectedFrameworkExtendedScores>
    {
        return {...this.m_scoresLookup};
    }

    private m_policyState: Lookup<WafAssessmentRunPolicyScanExtended>;

    private m_scanningPolicyUris: Set<string>;
    get scanningPolicyUris(): Set<string>
    {
        return this.m_scanningPolicyUris;
    }

    public readonly contentUpdated = new Subject<void>();

    get showSyncStatus(): boolean
    {
        return !!this.runExt?.model?.hyperscaler_workload_id && !!this?.model?.hyperscaler_framework_id;
    }

    get syncAWSDetails(): Models.WellArchitectedFrameworkCustomLensForAws
    {
        if (this.model.sync_with_hyperscaler instanceof Models.WellArchitectedFrameworkCustomLensForAws)
        {
            return this.model.sync_with_hyperscaler;
        }
        return null;
    }

    private constructor(public readonly model: Models.WellArchitectedFramework,
                        private m_pillars: WellArchitectedFrameworkPillarExtended[],
                        private readonly m_policyUris: Set<string>)
    {
        this.m_pillarLookup = UtilsService.extractLookup(m_pillars);
    }

    public getPillar(pillarId: string): WellArchitectedFrameworkPillarExtended
    {
        return this.m_pillarLookup[pillarId];
    }

    public getQuestion(pillarId: string,
                       questionId: string): WellArchitectedFrameworkQuestionExtended
    {
        const pillar = this.getPillar(pillarId);
        return pillar?.getQuestion(questionId);
    }

    public bucketProcessed(bucketId: string)
    {
        const pillarScores            = this.pillars.map((pillarExt) =>
                                                         {
                                                             pillarExt.markAsUnsynced();
                                                             return new WellArchitectedFrameworkPillarScore(pillarExt.model,
                                                                                                            pillarExt.icon,
                                                                                                            pillarExt.score,
                                                                                                            pillarExt.numAutomaticIssues,
                                                                                                            pillarExt.unverifiedAutomatedBestPractices,
                                                                                                            pillarExt.unverifiedManualBestPractices,
                                                                                                            pillarExt.applicable);
                                                         });
        this.m_scoresLookup[bucketId] = new WellArchitectedFrameworkExtendedScores(bucketId, this.score, pillarScores);
    }

    public updateScanningPolicies(contexts: Models.WellArchitectedFrameworkAssessmentContext[])
    {
        const hadPolicies         = !!this.m_scanningPolicyUris;
        this.m_scanningPolicyUris = null;

        const latestEntry = this.latestEntryExt?.model;
        contexts ||= latestEntry?.background_job && latestEntry.triggered_contexts;
        if (contexts)
        {
            this.m_scanningPolicyUris = new Set<string>();
            for (const context of contexts)
            {
                if (this.id === context.framework_id)
                {
                    for (const policy of this.collectPolicies(context)) this.m_scanningPolicyUris.add(policy);
                }
            }
        }

        const hasPolicies = !!this.m_scanningPolicyUris;
        if (hadPolicies !== hasPolicies)
        {
            for (const pillar of this.pillars) pillar.triggerContentUpdate();
        }
    }

    private collectPolicies(context: Models.WellArchitectedFrameworkAssessmentContext): Set<string>
    {
        const policies = new Set<string>();
        if (context.question_id)
        {
            this.getQuestion(context.pillar_id, context.question_id)
                ?.collectPolicies(context, policies);
        }
        else if (context.pillar_id)
        {
            this.getPillar(context.pillar_id)
                ?.collectPolicies(context, policies);
        }
        else
        {
            for (const pillar of this.pillars)
            {
                for (const question of pillar.questions) question.collectPolicies(context, policies);
            }
        }

        return policies;
    }

    public refreshAutomated()
    {
        for (const pillar of this.m_pillars) pillar.refreshAutomated();
    }

    public hasPolicy(policyUri: string): boolean
    {
        return this.m_policyUris.has(policyUri);
    }

    public updatePolicyState(policyState: Lookup<WafAssessmentRunPolicyScanExtended>)
    {
        const relevantPolicyState: Lookup<WafAssessmentRunPolicyScanExtended> = {};
        for (const id in policyState)
        {
            const state = policyState[id];
            if (state.context.framework_id === this.id) relevantPolicyState[id] = state;
        }
        this.m_policyState = relevantPolicyState;

        for (const pillarExt of this.pillars)
        {
            pillarExt.updatePolicyState(this.m_policyState);
        }
    }

    public applyFilter(filter: WafFrameworkQueryModel)
    {
        for (const pillar of this.m_pillars) pillar.applyFilter(filter);
        this.m_someMatchFilter = !filter || this.m_pillars.some((pillar) => pillar.matchesFilter);

        this.m_pillars = UtilsService.arrayCopy(this.m_pillars);
    }

    public syncStatusColor(status: SyncStatus)
    {
        if (status == SyncStatus.Completed)
        {
            return ChartColorUtilities.getColorById("Severity Colors", "low").color;
        }
        else
        {
            return ChartColorUtilities.getColorById("Severity Colors", "critical").color;
        }
    }

    //--//

    public static async newInstance(appDomain: AppDomainContext,
                                    model: Models.WellArchitectedFramework): Promise<WellArchitectedFrameworkExtended>
    {
        model         = Models.WellArchitectedFramework.deepClone(model);
        const fetcher = appDomain.wafFrameworks.getFrameworksFetcher(appDomain.users.lastActiveTenantId);

        const questionIds: Models.RecordIdentity[] = [];
        for (let pillar of model.pillars)
        {
            questionIds.push(...pillar.questions);
        }
        // Load all into cache
        const questions                                = await fetcher.getQuestions(questionIds);
        const bestPracticeIds: Models.RecordIdentity[] = [];
        for (let question of questions)
        {
            bestPracticeIds.push(...question.best_practices);
        }
        // Load all into cache
        await fetcher.getBestPractices(bestPracticeIds);

        const pillarExts   = await mapInParallel(model.pillars, (pillar) => WellArchitectedFrameworkPillarExtended.newInstance(fetcher, pillar));
        const frameworkExt = new WellArchitectedFrameworkExtended(model, pillarExts, fetcher.getPolicyUris(model.framework_id));
        for (const pillarExt of pillarExts) pillarExt.frameworkExt = frameworkExt;
        frameworkExt.pillars.sort((pillarA,
                                   pillarB) => UtilsService.compareStrings(pillarA.id, pillarB.id, true));

        return frameworkExt;
    }
}

export class WellArchitectedFrameworkPillarExtended
{
    get id(): string
    {
        return this.model.pillar_id;
    }

    get description(): string
    {
        return this.model.description;
    }

    get icon(): string
    {
        return this.model.icon;
    }

    get applicable(): boolean
    {
        return this.questions.some((question) => question.applicable);
    }

    get hasAutomaticBps(): boolean
    {
        return this.questions.some(question => question.hasAutomaticBps);
    }

    private m_policyScans: WafAssessmentRunPolicyScanExtended[];

    get policyLabel(): string
    {
        return this.model.pillar;
    }

    get policies(): WafAssessmentRunPolicyScanExtended[]
    {
        const allPolicies = this.m_policyScans?.filter((scan) => scan.model) || [];

        return allPolicies.filter(policyScan =>
                                  {
                                      const bpsWithPolicy = this.questions.flatMap(q => q.bestPractices)
                                                                .filter(bp =>
                                                                            bp.model.policy_uris?.includes(policyScan.model.policy_uri)
                                                                );

                                      if (!bpsWithPolicy.length) return true;

                                      const hasVisiblePolicyBp = bpsWithPolicy.some(bp => WellArchitectedFrameworkLayerWithState.hasVisiblePoliciesForBestPractice(bp, true));

                                      return hasVisiblePolicyBp;
                                  });
    }

    get activeScanRelevant(): boolean
    {
        return this.questions.some(question => question.activeScanRelevant);
    }

    get scanStatus(): ScanProgressState
    {
        if (this.activeScanRelevant) return this.frameworkExt.scanStatus;

        return ScanProgressState.Complete;
    }

    get runHasActiveScan(): boolean
    {
        return this.frameworkExt.runHasActiveScan;
    }

    private m_matchesFilter: boolean;
    get matchesFilter(): boolean
    {
        return this.m_matchesFilter;
    }

    frameworkExt: WellArchitectedFrameworkExtended;

    private m_synced: boolean = false;

    private m_label: string;
    get label(): string
    {
        if (!this.m_synced) this.recomputeInfo();

        return this.m_label;
    }

    get staticPillarLabel(): string
    {
        return `${this.model.pillar} (${this.questions.filter((question) => question.matchesFilter).length})`;
    }

    private m_numAutomaticIssues: number;
    get numAutomaticIssues(): number
    {
        if (!this.m_synced) this.recomputeInfo();

        return this.m_numAutomaticIssues;
    }

    private m_unverifiedAutomatedBestPractices: number;
    get unverifiedAutomatedBestPractices(): number
    {
        if (!this.m_synced) this.recomputeInfo();

        return this.m_unverifiedAutomatedBestPractices;
    }

    private m_unverifiedManualBestPractices: number;
    get unverifiedManualBestPractices(): number
    {
        if (!this.m_synced) this.recomputeInfo();

        return this.m_unverifiedManualBestPractices;
    }

    private m_score: number;
    get score(): number
    {
        if (!this.m_synced) this.recomputeInfo();

        return this.m_score;
    }

    private m_numCompletedBp: number;
    get numCompletedBp(): number
    {
        if (isNaN(this.m_numCompletedBp)) this.recomputeInfo();

        return this.m_numCompletedBp;
    }

    private m_numAutomatableBp: number;
    get numAutomatableBp(): number
    {
        if (isNaN(this.m_numAutomatableBp)) this.recomputeInfo();

        return this.m_numAutomatableBp;
    }

    private m_numAutomaticBp: number;
    get numAutomaticBp(): number
    {
        if (isNaN(this.m_numAutomaticBp)) this.recomputeInfo();

        return this.m_numAutomaticBp;
    }

    private m_numNotApplicableBp: number;
    get numNotApplicableBp(): number
    {
        if (isNaN(this.m_numNotApplicableBp)) this.recomputeInfo();

        return this.m_numNotApplicableBp;
    }

    private m_numBp: number;
    get numBp(): number
    {
        if (isNaN(this.m_numBp)) this.recomputeInfo();

        return this.m_numBp;
    }

    private readonly m_questionLookup: Lookup<WellArchitectedFrameworkQuestionExtended>;

    public readonly contentUpdated = new Subject<void>();

    private constructor(public readonly model: Models.WellArchitectedFrameworkPillar,
                        public readonly questions: WellArchitectedFrameworkQuestionExtended[])
    {
        this.m_questionLookup = UtilsService.extractLookup(this.questions);
    }

    public getQuestion(questionId: string): WellArchitectedFrameworkQuestionExtended
    {
        return this.m_questionLookup[questionId];
    }

    public setBaseStates(owner: string)
    {
        for (const question of this.questions)
        {
            question.setBaseStates(owner);
        }
    }

    public triggerContentUpdate()
    {
        for (const question of this.questions)
        {
            question.contentUpdated.next();
            for (const bp of question.bestPractices) bp.contentUpdated.next();
        }
        this.contentUpdated.next();
        if (this.frameworkExt) this.frameworkExt.contentUpdated.next();
    }

    public collectPolicies(context: Models.WellArchitectedFrameworkAssessmentContext,
                           policies: Set<string>)
    {
        for (const question of this.questions) question.collectPolicies(context, policies);
    }

    public markAsUnsynced()
    {
        this.m_synced = false;
    }

    private recomputeInfo()
    {
        let partialScore              = 0;
        let numNotApplicableQuestions = 0;
        let numNotApplicableBp        = 0;
        let numManualUnverifiedBp     = 0;
        let numAutomatedUnverifiedBp  = 0;
        let numAutomaticIssues        = 0;
        let numAutomaticBp            = 0;
        let numAutomatableBp          = 0;
        let numCompletedBp            = 0;
        let numBp                     = 0;
        for (const question of this.questions)
        {
            if (question.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable)
            {
                numNotApplicableQuestions++;
            }

            const nonNotaBps = (question.bestPractices || []).filter(bp => !bp.model?.is_none_of_the_above);
            for (let bestPractice of nonNotaBps)
            {
                if (bestPractice.nature === Models.WellArchitectedFrameworkBestPracticeNature.Automated) numAutomaticBp++;

                if (bestPractice.model?.is_automatable) numAutomatableBp++;

                if (bestPractice.verified && !bestPractice.questionNotApplicable)
                {
                    partialScore++;
                    numCompletedBp++;
                }
                else if (bestPractice.questionNotApplicable || !bestPractice.applicable)
                {
                    numNotApplicableBp++;
                    numCompletedBp++;
                }
                else
                {
                    switch (bestPractice.nature)
                    {
                        case Models.WellArchitectedFrameworkBestPracticeNature.Manual:
                            numManualUnverifiedBp++;
                            break;

                        case Models.WellArchitectedFrameworkBestPracticeNature.Automated:
                            numAutomatedUnverifiedBp++;
                            numAutomaticIssues += bestPractice.issues.length + bestPractice.errors.length;
                            break;
                    }
                }
            }
            numBp += nonNotaBps.length;
        }

        if (numNotApplicableQuestions === this.questions.length)
        {
            this.m_label = `${this.model.pillar} (N/A)`;
        }
        else
        {
            let numMatchingVerified      = 0;
            let numMatchingNotApplicable = 0;
            let numMatching              = 0;
            for (const question of this.questions)
            {
                if (question.matchesFilter)
                {
                    const nonNotaBps = question.bestPractices.filter(bp => !bp.model?.is_none_of_the_above);
                    if (nonNotaBps.length)
                    {
                        numMatching++;

                        if (nonNotaBps.every(bp => bp.verified))
                        {
                            numMatchingVerified++;
                        }
                        else if (question.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable)
                        {
                            numMatchingNotApplicable++;
                        }
                    }
                }
            }

            this.m_label = `${this.model.pillar} (${numMatchingVerified}/${numMatching - numMatchingNotApplicable})`;
        }

        this.m_numAutomaticBp                   = numAutomaticBp;
        this.m_numAutomatableBp                 = numAutomatableBp;
        this.m_numNotApplicableBp               = numNotApplicableBp;
        this.m_numCompletedBp                   = numCompletedBp;
        this.m_numAutomaticIssues               = numAutomaticIssues;
        this.m_unverifiedAutomatedBestPractices = numAutomatedUnverifiedBp;
        this.m_unverifiedManualBestPractices    = numManualUnverifiedBp;
        this.m_score                            = 100 * partialScore / (numBp - numNotApplicableBp) || 0;
        this.m_numBp                            = numBp;
    }

    public updatePolicyState(policyState: Lookup<WafAssessmentRunPolicyScanExtended>)
    {
        this.m_policyScans = [];
        for (const question of this.questions)
        {
            question.updatePolicyState(policyState);
        }

        for (const id in policyState)
        {
            const state = policyState[id];
            if (state.matchesPillar(this.frameworkExt, this.model))
            {
                if (state.model) this.m_policyScans.push(state);
            }
        }
    }

    public refreshAutomated()
    {
        for (const question of this.questions) question.refreshAutomated();
    }

    public applyFilter(filter: WafFrameworkQueryModel)
    {
        for (const question of this.questions) question.applyFilter(filter);

        this.m_matchesFilter = !filter || this.questions.some((question) => question.matchesFilter);
    }

    //--//

    public static async newInstance(fetcher: WafTenantFetcher,
                                    model: Models.WellArchitectedFrameworkPillar): Promise<WellArchitectedFrameworkPillarExtended>
    {
        model              = Models.WellArchitectedFrameworkPillar.deepClone(model);
        const questions    = await fetcher.getQuestions(model.questions);
        const questionExts = await mapInParallel(questions || [], (question) => WellArchitectedFrameworkQuestionExtended.newInstance(fetcher, question));
        const pillarExt    = new WellArchitectedFrameworkPillarExtended(model, questionExts);
        for (const questionExt of questionExts) questionExt.pillarExt = pillarExt;

        return pillarExt;

    }

    public static computePillarId(frameworkId: string,
                                  pillarId: string): string
    {
        return `${frameworkId}/${pillarId}`;
    }
}

export abstract class WellArchitectedFrameworkLayerWithState
{
    protected m_policyState: Lookup<WafAssessmentRunPolicyScanExtended>;

    abstract activeScanRelevant: boolean;

    abstract get scanStatus(): ScanProgressState;

    get effectiveScanStatus(): ScanProgressState
    {
        if (this.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable)
        {
            return ScanProgressState.Complete;
        }
        return this.scanStatus;
    }

    public static hasVisiblePoliciesForBestPractice(bestPractice: WellArchitectedFrameworkBestPracticeExtended,
                                                    requireApplicableQuestion: boolean = false): boolean
    {
        if (!bestPractice) return false;
        if (requireApplicableQuestion && !bestPractice.questionExt?.applicable) return false;

        return bestPractice.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable &&
               bestPractice.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException;
    }

    //--//

    public comments: Comment[] = [];

    public owner: string;
    public assignedTo: string[]    = [];
    public attachmentIds: string[] = [];
    public syncStatus: SyncStatus;
    public syncFailReason: string;

    protected m_recommendation: string;

    protected m_status: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus = Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open;
    get status(): Models.WellArchitectedFrameworkAssessmentRunAnswerStatus
    {
        return this.m_status;
    }

    set status(status: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus)
    {
        this.m_status = status;
    }

    get applicable(): boolean
    {
        return this.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
    }

    get verified(): boolean
    {
        const status = this.status;
        return status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified ||
               status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException;
    }

    attachments: Models.Attachment[];

    contentUpdated = new Subject<void>();

    public processAnswer(answer: Models.WellArchitectedFrameworkAssessmentRunAnswer)
    {
        const comment = WellArchitectedFrameworkLayerWithState.generateComment(answer);
        if (comment) this.comments.unshift(comment);

        if (answer.recommendation)
        {
            this.m_recommendation = answer.recommendation;
        }

        this.updateState(answer.owner, answer.assigned_to, answer.status, answer.attachment_ids);
    }

    public updateState(owner: string,
                       assignedTo: string[],
                       state: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus,
                       attachmentIds: string[])
    {
        if (owner) this.owner = owner;
        if (assignedTo) this.assignedTo = assignedTo;
        if (state) this.m_status = state;
        if (attachmentIds) this.attachmentIds = attachmentIds;
        this.contentUpdated.next();
    }

    public updateSyncStatus(answer: Models.WellArchitectedFrameworkAssessmentRunAnswer)
    {
        if (answer.sync_failure_reason)
        {
            this.syncStatus     = SyncStatus.Failed;
            this.syncFailReason = answer.sync_failure_reason;
        }
        else
        {
            this.syncStatus     = SyncStatus.Completed;
            this.syncFailReason = null;
        }

        this.contentUpdated.next();
    }

    public async ensureAttachments(attachmentsApi: AttachmentsApi): Promise<boolean>
    {
        if (!this.attachments)
        {
            this.attachments = this.attachmentIds?.length && await attachmentsApi.batch(Models.BatchRequest.newInstance({ids: this.attachmentIds})) || [];
        }

        return !!this.attachments?.length;
    }

    public appendComment(answer: Models.WellArchitectedFrameworkAssessmentRunAnswer,
                         user: string)
    {
        this.comments.unshift(WellArchitectedFrameworkLayerWithState.generateComment(answer, user));
        this.comments = UtilsService.arrayCopy(this.comments);
    }

    protected formatQuestionId(pillarId: string,
                               questionId: string): string
    {
        let separator = ".";
        if (isNaN(+pillarId))
        {
            pillarId  = pillarId.toUpperCase();
            separator = " ";
        }
        else
        {
            pillarId = `${+pillarId}`;
        }
        if (!isNaN(+questionId))
        {
            questionId = `${+questionId}`;
        }
        return `${pillarId}${separator}${questionId}`;
    }

    //--//

    public static generateComment(answer: Models.WellArchitectedFrameworkAssessmentRunAnswer,
                                  user?: string): Comment
    {
        user = answer.createdBy || user;

        let comment: Comment;
        if (answer.comment && user)
        {
            comment = {
                user   : user,
                date   : answer.createdOn || new Date(),
                comment: answer.comment
            };
            if (answer.status) comment.prefix = `Status: ${WafGetStatusFriendly(answer.status)}`;
        }

        return comment;
    }
}

export class WellArchitectedFrameworkQuestionExtended extends WellArchitectedFrameworkLayerWithState
{
    get id(): string
    {
        return this.model.question_id;
    }

    private m_matchesFilter: boolean = true;
    get matchesFilter(): boolean
    {
        return this.m_matchesFilter;
    }

    pillarExt: WellArchitectedFrameworkPillarExtended;

    private readonly m_bestPracticesLookup: Lookup<WellArchitectedFrameworkBestPracticeExtended>;
    public assessmentRunQuestion: WafAssessmentRunQuestionExtended;

    private m_risk: Models.WellArchitectedFrameworkRisk;
    get risk(): Models.WellArchitectedFrameworkRisk
    {
        if (!this.m_risk)
        {
            this.m_risk = Models.WellArchitectedFrameworkRisk.None;
            if (this.questionApplicable && this.bestPractices.length)
            {
                const nonNotaBps = this.bestPractices.filter(bp => !bp.model?.is_none_of_the_above);

                if (nonNotaBps.length)
                {
                    const allNonNotaBpsVerified = nonNotaBps.every(bp => bp.verified);

                    if (allNonNotaBpsVerified)
                    {
                        this.m_risk = Models.WellArchitectedFrameworkRisk.None;
                    }
                    else
                    {
                        let maxRisk = WellArchitectedFrameworkBestPracticeExtended.numericalRisk(this.m_risk);
                        for (const bp of nonNotaBps)
                        {
                            if (bp.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open ||
                                bp.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered)
                            {
                                const risk          = bp.risk;
                                const numericalRisk = WellArchitectedFrameworkBestPracticeExtended.numericalRisk(risk);
                                if (numericalRisk > maxRisk)
                                {
                                    maxRisk     = numericalRisk;
                                    this.m_risk = risk;
                                }
                            }
                        }
                    }
                }
            }
        }

        return this.m_risk;
    }

    get questionApplicable(): boolean
    {
        return super.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
    }

    set questionApplicable(applicable: boolean)
    {
        this.m_status = applicable ? Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open : Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
    }

    get hasAutomaticBps(): boolean
    {
        return !!this.m_policyState;
    }

    get frameworkExt(): WellArchitectedFrameworkExtended
    {
        return this.pillarExt.frameworkExt;
    }

    get runHasActiveScan(): boolean
    {
        return this.frameworkExt.runHasActiveScan;
    }

    get activeScanRelevant(): boolean
    {
        return this.bestPractices.some((bestPractice) => bestPractice.activeScanRelevant);
    }

    get scanStatus(): ScanProgressState
    {
        if (this.activeScanRelevant) return this.frameworkExt.scanStatus;

        return ScanProgressState.Complete;
    }

    private m_aggregatedStatus: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus;

    get status(): Models.WellArchitectedFrameworkAssessmentRunAnswerStatus
    {
        if (!this.questionApplicable)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
        }
        this.m_aggregatedStatus = null;

        const isNotaSelected = this.isNotaSelected();
        if (isNotaSelected)
        {
            const nonNotaBps         = this.bestPractices.filter(bp => !bp.model.is_none_of_the_above);
            const allNonNotaBpsAreNA = nonNotaBps.length > 0 &&
                                       nonNotaBps.every(bp => bp.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable);

            if (allNonNotaBpsAreNA)
            {
                this.m_aggregatedStatus = Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
                return this.m_aggregatedStatus;
            }
            this.m_aggregatedStatus = Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open;
            return this.m_aggregatedStatus;
        }

        const manualUnansweredStatus = this.getManualUnansweredStatus();
        if (manualUnansweredStatus !== null)
        {
            this.m_aggregatedStatus = manualUnansweredStatus;
            return this.m_aggregatedStatus;
        }
        this.m_aggregatedStatus = this.calculateNormalStatus();

        return this.m_aggregatedStatus;
    }

    private isNotaSelected(): boolean
    {
        for (const bp of this.bestPractices)
        {
            if (bp.model.is_none_of_the_above)
            {
                return bp.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified ||
                       bp.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException;
            }
        }
        return false;
    }

    private getManualUnansweredStatus(): Models.WellArchitectedFrameworkAssessmentRunAnswerStatus | null
    {
        const allBpsManual = this.bestPractices.every(bp => bp.manual);
        if (!allBpsManual)
        {
            return null;
        }

        const answers = this.assessmentRunQuestion?.model?.answers || [];
        if (answers.length === 0)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered;
        }

        let allApplicableBpsOpen = true;
        let hasApplicableBPs     = false;

        for (const bp of this.bestPractices)
        {
            if (bp.model.is_none_of_the_above || !bp.applicable)
            {
                continue;
            }

            hasApplicableBPs = true;
            if (bp.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open)
            {
                allApplicableBpsOpen = false;
                break;
            }
        }

        if (hasApplicableBPs && allApplicableBpsOpen)
        {
            const latestBPAnswers                                                        = new Map<string, Models.WellArchitectedFrameworkAssessmentRunAnswer>();
            let latestQuestionAnswer: Models.WellArchitectedFrameworkAssessmentRunAnswer = null;

            for (const answer of answers.sort((a,
                                               b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()))
            {
                if (answer.best_practice_id)
                {
                    latestBPAnswers.set(answer.best_practice_id, answer);
                }
                else
                {
                    latestQuestionAnswer = answer;
                }
            }

            const hasCurrentUnansweredBP = Array.from(latestBPAnswers.values())
                                                .some(answer =>
                                                          answer.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered);

            const hasUnansweredQuestionAnswer = latestQuestionAnswer?.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered;

            if (hasCurrentUnansweredBP || hasUnansweredQuestionAnswer)
            {
                return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered;
            }
        }

        return null;
    }

    private calculateNormalStatus(): Models.WellArchitectedFrameworkAssessmentRunAnswerStatus
    {
        let hasOpen                  = false;
        let hasVerifiedWithException = false;
        let hasVerified              = false;
        let allNotApplicable         = true;
        let hasApplicableBPs         = false;

        for (const bp of this.bestPractices)
        {
            if (bp.model.is_none_of_the_above)
            {
                continue;
            }

            if (bp.applicable)
            {
                hasApplicableBPs = true;
                allNotApplicable = false;

                switch (bp.status)
                {
                    case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open:
                    case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered:
                        hasOpen = true;
                        break;
                    case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException:
                        hasVerifiedWithException = true;
                        break;
                    case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified:
                        hasVerified = true;
                        break;
                }
            }
            else
            {
                if (!bp.model.is_none_of_the_above)
                {
                    allNotApplicable = allNotApplicable && true;
                }
            }
        }

        if (!hasApplicableBPs && allNotApplicable)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable;
        }

        if (hasOpen)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open;
        }

        if (hasVerifiedWithException)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException;
        }

        if (hasVerified)
        {
            return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified;
        }

        return Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open;
    }

    get formQuestion()
    {
        return this.formatQuestionId(this.model.pillar_id, this.model.question_id);
    }

    get questionText(): string
    {
        const questionId = this.formQuestion;
        const question   = this.model.question;

        return `${questionId}. ${question}`;
    }

    get policyLabel(): string
    {
        return this.model.question;
    }

    get policies(): WafAssessmentRunPolicyScanExtended[]
    {
        const allPolicies = UtilsService.mapLookup(this.m_policyState, (key,
                                                                        value) => value.model ? value : null, true);

        return allPolicies.filter(policyScan =>
                                  {
                                      const bpsWithPolicy = this.bestPractices.filter(bp =>
                                                                                          bp.model.policy_uris?.includes(policyScan.model.policy_uri)
                                      );

                                      if (!bpsWithPolicy.length) return true;

                                      const hasVisiblePolicyBp = bpsWithPolicy.some(bp => WellArchitectedFrameworkLayerWithState.hasVisiblePoliciesForBestPractice(bp));

                                      return hasVisiblePolicyBp;
                                  });
    }

    private constructor(public readonly model: Models.WellArchitectedFrameworkQuestion,
                        public readonly bestPractices: WellArchitectedFrameworkBestPracticeExtended[])
    {
        super();

        this.m_bestPracticesLookup = UtilsService.extractLookup(bestPractices);
    }

    public updatePolicyState(policyState: Lookup<WafAssessmentRunPolicyScanExtended>)
    {
        this.m_policyState = null;
        if (policyState)
        {
            for (const bp of this.bestPractices.filter((bestPractice) => bestPractice.automated))
            {
                bp.updatePolicyState(policyState);
            }

            if (this.bestPractices.some((bestPractice) => bestPractice.automated))
            {
                this.m_policyState = {};
                for (const context in policyState)
                {
                    const state = policyState[context];
                    if (state.matchesQuestion(this.model))
                    {
                        this.m_policyState[context] = state;
                    }
                }

                this.contentUpdated.next();
            }
        }
    }

    public getBestPractice(bestPracticeId: string): WellArchitectedFrameworkBestPracticeExtended
    {
        return this.m_bestPracticesLookup[bestPracticeId];
    }

    public processAnswers(answers: Models.WellArchitectedFrameworkAssessmentRunAnswer[],
                          processIndividualAnswer = true)
    {
        for (const answer of answers || [])
        {
            if (answer.best_practice_id)
            {
                const bpExt = this.getBestPractice(answer.best_practice_id);
                if (bpExt)
                {
                    if (answer.status)
                    {
                        bpExt.status = answer.status;
                    }

                    bpExt.updateSyncStatus(answer);

                    if (processIndividualAnswer)
                    {
                        bpExt.processAnswer(answer);
                    }
                }
            }
            else
            {
                if (processIndividualAnswer)
                {
                    this.processAnswer(answer);
                }
            }
        }
        this.contentUpdated.next();

        if (this.pillarExt)
        {
            this.pillarExt.contentUpdated.next();
            if (this.pillarExt.frameworkExt) this.pillarExt.frameworkExt.contentUpdated.next();
        }
    }

    public processQuestion(question: WafAssessmentRunQuestionExtended)
    {
        this.assessmentRunQuestion = question;
        let hasFailure             = question.model.sync_failure_reason;
        for (const answer of question.model.answers || [])
        {
            if (!answer?.best_practice_id && answer.status)
            {
                if (hasFailure)
                {
                    this.syncStatus     = SyncStatus.Failed;
                    this.syncFailReason = question.model.sync_failure_reason;
                }
                else
                {
                    this.syncStatus     = SyncStatus.Completed;
                    this.syncFailReason = null;
                }
            }
        }
    }

    public setBaseStates(owner: string)
    {
        this.comments = [];
        this.updateState(owner, [owner], Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open, []);
        for (const bp of this.bestPractices)
        {
            bp.comments = [];
            bp.updateState(owner, [owner], Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open, []);
        }
    }

    public stateChanged()
    {
        this.m_risk             = null;
        this.m_aggregatedStatus = null;
    }

    public refreshAutomated()
    {
        let someQueued = false;
        for (const bp of this.bestPractices)
        {
            if (bp.automated)
            {
                someQueued = true;
                bp.contentUpdated.next();
            }
        }
        if (someQueued) this.contentUpdated.next();
    }

    public applyFilter(filter: WafFrameworkQueryModel)
    {
        let someChanged = false;
        for (const bestPractice of this.bestPractices)
        {
            if (bestPractice.applyFilter(filter)) someChanged = true;
        }

        if (someChanged) this.contentUpdated.next();

        this.m_matchesFilter = !filter || filter.matches(this) || this.someMatchFilter();
    }

    public someMatchFilter(): boolean
    {
        return this.bestPractices.some((bestPractice) => bestPractice.matchesFilter);
    }

    public collectPolicies(context: Models.WellArchitectedFrameworkAssessmentContext,
                           policies: Set<string>)
    {
        if (context.best_practice_id)
        {
            this.m_bestPracticesLookup[context.best_practice_id]?.collectPolicies(policies);
        }
        else
        {
            for (const bestPractice of this.bestPractices) bestPractice.collectPolicies(policies);
        }
    }

    //--//

    public static async newInstance(fetcher: WafTenantFetcher,
                                    model: Models.WellArchitectedFrameworkQuestion): Promise<WellArchitectedFrameworkQuestionExtended>
    {
        model                  = Models.WellArchitectedFrameworkQuestion.deepClone(model);
        const bestPractices    = await fetcher.getBestPractices(model.best_practices);
        const bestPracticeExts = bestPractices?.map((bestPractice) => new WellArchitectedFrameworkBestPracticeExtended(bestPractice));
        const questionExt      = new WellArchitectedFrameworkQuestionExtended(model, bestPracticeExts || []);
        for (const bpExt of questionExt.bestPractices) bpExt.questionExt = questionExt;

        return questionExt;
    }

    public static computeQuestionId(question: Models.WellArchitectedFrameworkQuestion): string
    {
        return `${WellArchitectedFrameworkPillarExtended.computePillarId(question.framework_id, question.pillar_id)}/${question.question_id}`;
    }
}

export class WellArchitectedFrameworkBestPracticeExtended extends WellArchitectedFrameworkLayerWithState
{
    get id(): string
    {
        return this.model.best_practice_id;
    }

    private m_matchesFilter: boolean = true;
    get matchesFilter(): boolean
    {
        return this.m_matchesFilter;
    }

    private m_policyScans: WafAssessmentRunPolicyScanExtended[];

    private m_effectivelyManual: boolean;

    get automated(): boolean
    {
        return this.nature === Models.WellArchitectedFrameworkBestPracticeNature.Automated;
    }

    get manual(): boolean
    {
        return this.nature === Models.WellArchitectedFrameworkBestPracticeNature.Manual;
    }

    get nature(): Models.WellArchitectedFrameworkBestPracticeNature
    {
        return this.m_effectivelyManual ? Models.WellArchitectedFrameworkBestPracticeNature.Manual : this.model.nature;
    }

    get risk(): Models.WellArchitectedFrameworkRisk
    {
        return this.model.risk;
    }

    get frameworkExt(): WellArchitectedFrameworkExtended
    {
        return this.questionExt.frameworkExt;
    }

    public get recommendation(): string
    {
        return this.m_recommendation || this.model.recommendation;
    }

    public set recommendation(recommendation: string)
    {
        this.m_recommendation = recommendation;
    }

    get policyLabel(): string
    {
        return this.model.best_practice;
    }

    get policies(): WafAssessmentRunPolicyScanExtended[]
    {
        if (this.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable ||
            this.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException ||
            this.questionNotApplicable)
        {
            return [];
        }

        return this.m_policyScans?.filter((scan) => scan.model) || [];
    }

    get issues(): WafAssessmentRunPolicyScanExtended[]
    {
        return this.m_policyScans?.filter((scan) => scan.model?.violated_resource_count) || [];
    }

    get errors(): WafAssessmentRunPolicyScanExtended[]
    {
        return this.m_policyScans?.filter((scan) => scan.model?.failure) || [];
    }

    get runHasActiveScan(): boolean
    {
        return this.questionExt.frameworkExt.runHasActiveScan;
    }

    get activeScanRelevant(): boolean
    {
        const scanningPolicies = this.questionExt.frameworkExt.scanningPolicyUris;
        return !!scanningPolicies && this.model?.policy_uris?.some((policy) => scanningPolicies.has(policy));
    }

    get scanStatus(): ScanProgressState
    {
        if (this.activeScanRelevant) return this.questionExt.frameworkExt.scanStatus;

        return ScanProgressState.Complete;
    }

    get effectiveScanStatus(): ScanProgressState
    {
        if (this.status === Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable)
        {
            return ScanProgressState.Complete;
        }

        if (this.questionNotApplicable)
        {
            return ScanProgressState.Complete;
        }

        return this.scanStatus;
    }

    get formQuestion()
    {
        const questionId   = this.formatQuestionId(this.model.pillar_id, this.model.question_id);
        let bestPracticeId = this.model.best_practice_id;
        if (!isNaN(+this.model.best_practice_id))
        {
            bestPracticeId = `${+this.model.best_practice_id}`;
        }

        return `${questionId}.${bestPracticeId}`;
    }

    get bestPracticeName(): string
    {
        const bestPractice = this.model.best_practice;

        return `${this.formQuestion} ${bestPractice}`;
    }

    public readonly model: Models.WellArchitectedFrameworkBestPractice;

    questionExt: WellArchitectedFrameworkQuestionExtended;

    get questionNotApplicable(): boolean
    {
        return !this.questionExt?.questionApplicable;
    }

    get isNotaChecked(): boolean
    {
        const runQuestion = this.questionExt?.assessmentRunQuestion;
        if (!runQuestion) return false;

        const answers = runQuestion.model?.answers ?? [];
        const notaBP  = this.questionExt.bestPractices.find(bp => bp.model.is_none_of_the_above);
        if (!notaBP) return false;

        const notaAnswers = answers.filter(ans =>
                                               ans.best_practice_id === notaBP.model.best_practice_id);

        if (!notaAnswers.length) return false;

        const latestNotaAnswer = notaAnswers.reduce((latest,
                                                     current) =>
                                                        new Date(current.createdOn).getTime() > new Date(latest.createdOn).getTime() ? current : latest
        );

        const isNotaVerified = [
            Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified,
            Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException
        ].includes(latestNotaAnswer.status);

        const hasOtherVerifiedBP = this.questionExt.bestPractices.some(bp =>
                                                                       {
                                                                           if (bp.model.is_none_of_the_above) return false;

                                                                           const isVerified = this.isBPVerified(bp);
                                                                           return isVerified;
                                                                       });

        if (hasOtherVerifiedBP)
        {
            return false;
        }

        return isNotaVerified;
    }

    private isBPVerified(bp: WellArchitectedFrameworkBestPracticeExtended): boolean
    {
        const runQuestion = this.questionExt?.assessmentRunQuestion;
        if (!runQuestion) return false;

        const answers = runQuestion.model?.answers ?? [];

        const bpAnswers = answers.filter(ans =>
                                             ans.best_practice_id === bp.model.best_practice_id);

        if (!bpAnswers.length) return false;

        const latestAnswer = bpAnswers.reduce((latest,
                                               current) =>
                                                  new Date(current.createdOn).getTime() > new Date(latest.createdOn).getTime() ? current : latest
        );

        return [
            Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified,
            Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException
        ].includes(latestAnswer.status);
    }

    constructor(model: Models.WellArchitectedFrameworkBestPractice)
    {
        super();

        this.model = Models.WellArchitectedFrameworkBestPractice.deepClone(model);
    }

    public applyFilter(filter: WafFrameworkQueryModel): boolean
    {
        let prevMatch        = this.m_matchesFilter;
        this.m_matchesFilter = !filter || filter.matches(this);
        return this.m_matchesFilter !== prevMatch;
    }

    public collectPolicies(policies: Set<string>)
    {
        for (const policy of this.model?.policy_uris || []) policies.add(policy);
    }

    public updatePolicyState(policyState: Lookup<WafAssessmentRunPolicyScanExtended>)
    {
        this.m_policyScans = [];
        this.m_policyState = {};
        for (const id in policyState)
        {
            const state = policyState[id];
            if (state.matchesBestPractice(this.model))
            {
                this.m_policyState[id] = state;
                if (state.model) this.m_policyScans.push(state);
            }
        }

        if (this.questionExt.frameworkExt.runExt.firstScanComplete && this.m_effectivelyManual == null)
        {
            // If policy is automated but we are not running scans because of account config. Treat as manual
            this.m_effectivelyManual = !this.m_policyScans.length;
        }
    }

    //--//

    public static numericalRisk(risk: Models.WellArchitectedFrameworkRisk): number
    {
        switch (risk)
        {
            case Models.WellArchitectedFrameworkRisk.High:
                return 3;

            case Models.WellArchitectedFrameworkRisk.Medium:
                return 2;

            case Models.WellArchitectedFrameworkRisk.Low:
                return 1;
        }

        return 0;
    }

    public static computeBestPracticeId(bestPractice: Models.WellArchitectedFrameworkBestPractice): string
    {
        const parent = Models.WellArchitectedFrameworkQuestion.newInstance({
                                                                               framework_id: bestPractice.framework_id,
                                                                               pillar_id   : bestPractice.pillar_id,
                                                                               question_id : bestPractice.question_id
                                                                           });
        return `${WellArchitectedFrameworkQuestionExtended.computeQuestionId(parent)}/${bestPractice.best_practice_id}`;
    }
}

export class PolicyIssue
{
    private m_scan: WafAssessmentRunPolicyScanExtended;
    public filteredScans: WafAssessmentRunPolicyScanExtended[];

    public get scan(): WafAssessmentRunPolicyScanExtended
    {
        return this.m_scan;
    }

    constructor(public readonly cloudAccount: string,
                public readonly validAccountStatus: boolean,
                public readonly service: Models.CloudServiceName,
                public readonly scans: WafAssessmentRunPolicyScanExtended[])
    {}

    public async selectPolicy(host: BaseApplicationComponent,
                              uri: string,
                              updateViolations: boolean = true)
    {
        this.m_scan = this.scans.find(s => s.model.policy_uri === uri && s.model.policy_uri === uri);
        if (updateViolations)
        {
            if (this.m_scan?.model.violated_resource_count) await this.m_scan.ensureViolations(host);
        }
        else
        {
            this.m_scan.jobExecutionOutputDetails = await host.app.domain.apis.guardrails.jobDetails(host.app.domain.users.lastActiveTenantId, this.m_scan.model.policy_job_id);
            this.m_scan.violationsLoading         = false;
        }

    }

    public static filterPolicyByPillar(issues: PolicyIssue[],
                                       pillars: string[],
                                       jobIdPillarLookup: Lookup<string[]>): PolicyIssue[]
    {
        for (const issue of issues)
        {
            issue.filteredScans = issue.scans;
            if (pillars.length)
            {
                issue.filteredScans = issue.scans.filter(scan => pillars.some(pillar => jobIdPillarLookup[scan.model.policy_job_id]?.includes(pillar)));
            }
        }
        return issues;
    }
}

export enum SyncStatus
{
    Completed = "Success",
    Failed    = "Failed"
}
